import { sysCustomize } from "@adltools/adl-customize";
import {
  createVEditor,
  CustomContext,
  Customize,
  VEditor,
} from "@adltools/adl-veditor";
import { DDSMMSYYYY_FORMAT } from "@adltools/dateformats";
import { action, makeObservable, observable, runInAction } from "mobx";

import { makeUnit, Paginated, Unit } from "../../../adl-gen/common";
import {
  DbResult,
  DbRow,
  makeDbResult,
  QueryReq,
  Table,
} from "../../../adl-gen/common/adminui/api";
import { DbKey, snDbKey, WithDbId } from "../../../adl-gen/common/db";
import * as common_http from "../../../adl-gen/common/http";
import {
  FieldPredicate,
  makeFieldIn,
  makeSortField,
  makeTableQuery,
  makeTableView,
  SortDirection,
  SortField,
  TableView,
} from "../../../adl-gen/common/tabular";
import { ATypeExpr, DeclResolver } from "../../../adl-gen/runtime/adl";
import { createJsonBinding, JsonBinding } from "../../../adl-gen/runtime/json";
import { scopedNamesEqual } from "../../../adl-gen/runtime/utils";
import * as adlast from "../../../adl-gen/sys/adlast";

import { dbKeyFieldFns, dbKeyVEditor } from "./dbkey";
import {
  resolvePlacementTableFilter,
  resolvePlacementTableSorting,
  sortPlacementRowsByPublisherName,
  getPlacementPublisherId,
} from "./placementTableFilter";
import { AdminService } from "./service";
import { toPlacementExportJson } from "./utils/placementJson";

const PLACEMENT_QUERY_BATCH_SIZE = 500;

// DbRow consists of ADL serialized values using the standard
// schema (https://github.com/timbod7/adl/blob/master/docs/serialization.md)
//
// The typescript ADL forms and table tooling is in terms of the typescript
// in-memory representation. We'll define a type for that for clarity.

export interface TSRow {
  [key: string]: unknown;
}

export type TableHrefFn = (tablename: string) => string;

export type Loading<T> = { kind: "loading" } | { kind: "ready"; value: T };

export interface Metadata {
  tableNames: string[];
  tableMap: { [name: string]: Table | undefined };
  resolver: DeclResolver;
}

export interface TableMetadata {
  table: Table;
  resolver: DeclResolver;
  veditor: VEditor<TSRow, unknown, unknown>;
  jsonBinding: JsonBinding<TSRow>;
  customize: Customize;
}

/**
 * Append a stable `id` tiebreaker to a sort spec. Without a deterministic
 * ORDER BY, Postgres can return rows in a different order between identical
 * offset/limit queries, which makes pagination (and OFFSET-based batch scans)
 * skip, duplicate or reshuffle rows. That is what made deselected placement
 * rows appear to "re-select" after paging away and back.
 */
const withStableIdSort = (sorting: SortField[]): SortField[] => {
  if (sorting.some((s) => s.field === "id")) {
    return sorting;
  }
  return [
    ...sorting,
    makeSortField({ field: "id", direction: SortDirection.ascending }),
  ];
};

export class AdminStore {
  metadata: Loading<Metadata>;
  tableName: string | null;
  lastQuery: {
    tmetadata: TableMetadata;
    view: TableView;
    query: QueryReq;
  } | null;
  loadedRows: Paginated<WithDbId<TSRow>> | null;
  rowsLoading: boolean | undefined;
  columnPropsShown: string | null;
  /** Raw config from API per org id; used when parsed value omits allowedPublisherIdsForSearch. */
  organisationRawConfigByKey: Record<string, unknown> = {};
  /** Publisher id -> name, populated when viewing the placement table. */
  publisherNameById: Record<string, string> = {};
  // eslint-disable-next-line no-use-before-define
  actions: ActionData[] = [];
  pageSize: number = 20;
  /** Placement IDs selected for JSON export (persists across pagination). */
  selectedPlacementIds = observable.set<string>();
  placementSelectAllLoading = false;

  constructor(
    readonly service: AdminService,
    readonly tableHrefFn: TableHrefFn,
    // eslint-disable-next-line no-use-before-define
    readonly actionFactories: ActionFactory[],
    private readonly appDeclResolver: DeclResolver
  ) {
    makeObservable(this, {
      metadata: observable.ref,
      tableName: observable,
      lastQuery: observable.deep,
      loadedRows: observable.deep,
      rowsLoading: observable,
      columnPropsShown: observable,
      organisationRawConfigByKey: observable,
      publisherNameById: observable,
      selectedPlacementIds: observable,
      placementSelectAllLoading: observable,
      reloadMetadata: action,
      setTable: action,
      loadRows: action,
      setView: action,
      setColumnPropsShown: action,
      create: action,
      update: action,
      delete: action,
      pageForward: action,
      pageBack: action,
      setPlacementSelection: action,
      clearPlacementExportSelection: action,
      toggleSelectAllPlacementsMatchingFilter: action,
    });
    this.metadata = { kind: "loading" };
    this.tableName = null;
    this.lastQuery = null;
    this.loadedRows = null;
    this.columnPropsShown = null;
    void this.reloadMetadata();
  }

  reloadMetadata = async () => {
    const [tables, decls] = await Promise.all([
      this.service.adminQueryTables(makeTableQuery({})),
      this.service.adminQueryDecls(makeTableQuery({})),
    ]);
    const declMap: { [key: string]: adlast.ScopedDecl | undefined } = {};
    decls.items.forEach((mdecl) => {
      const sdecl = { moduleName: mdecl.moduleName, decl: mdecl.decl };
      declMap[sdecl.moduleName + "." + sdecl.decl.name] = sdecl;
    });

    const tableMap: { [name: string]: Table } = {};
    tables.items.forEach((table) => {
      tableMap[table.name] = table;
    });

    const mergedResolver = (sname: adlast.ScopedName): adlast.ScopedDecl => {
      const decl = declMap[sname.moduleName + "." + sname.name];
      if (decl !== undefined) {
        return decl;
      }
      return this.appDeclResolver(sname);
    };

    const metadata = {
      tableNames: tables.items.map((t) => t.name),
      tableMap,
      resolver: mergedResolver,
    };
    this.metadata = { kind: "ready", value: metadata };
    if (this.tableName !== null) {
      await this.initialLoad(metadata, this.tableName);
    }
  };

  setTable = async (tableName: string): Promise<void> => {
    this.tableName = tableName;
    if (tableName.toLowerCase() !== "placement") {
      this.publisherNameById = {};
      this.clearPlacementExportSelection();
    }
    if (this.metadata.kind === "ready") {
      await this.initialLoad(this.metadata.value, tableName);
    }
  };

  initialLoad = async (
    // eslint-disable-next-line no-use-before-define
    metadata: Metadata,
    tableName: string
  ): Promise<void> => {
    const tmetadata = createTableMetadata(
      metadata,
      tableName,
      this.tableHrefFn
    );
    this.lastQuery = {
      tmetadata,
      view: makeTableView({
        columns: [],
      }),
      query: {
        table: tmetadata.table.name,
        columns: [],
        query: makeTableQuery({
          offset: 0,
          count: this.pageSize,
        }),
      },
    };
    await this.loadRows();
  };

  loadRows = async (): Promise<void> => {
    if (this.lastQuery !== null) {
      const tmetadata = this.lastQuery.tmetadata;
      this.rowsLoading = true;
      this.lastQuery.query.query.filter = await this.resolveTableQueryFilter(
        tmetadata.table.name,
        this.lastQuery.view.filter
      );
      const { apiSorting, publisherNameSorting } = resolvePlacementTableSorting(
        this.lastQuery.view.sorting
      );
      this.lastQuery.query.query.sorting = withStableIdSort(apiSorting);
      const dbrows = await this.service.adminQuery(this.lastQuery.query);
      const nextRawConfig: Record<string, unknown> = {};
      if (tmetadata.table.name === "organisation") {
        for (const dbrow of dbrows.items) {
          const config = dbrow.value?.config;
          if (config !== undefined && config !== null) {
            nextRawConfig[dbrow.id] = config;
          }
        }
      }
      this.organisationRawConfigByKey = nextRawConfig;
      // Parse each row independently. A single row with data that doesn't match
      // the current ADL schema (e.g. a legacy/corrupt placement spec) must not
      // break the whole page — fall back to the raw value so the rest of the
      // page (and pagination) keeps working.
      const tsrows = dbrows.items.map((dbrow) => {
        const id = dbrow.id;
        try {
          const value = tmetadata.jsonBinding.fromJsonE(dbrow.value);
          return { id, value };
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn(
            `Failed to parse ${tmetadata.table.name} row ${id}; showing raw value.`,
            e
          );
          return { id, value: (dbrow.value ?? {}) as TSRow };
        }
      });
      if (tmetadata.table.name.toLowerCase() === "placement") {
        await this.cachePublisherNamesForPlacements(tsrows);
      }
      const sortedRows =
        tmetadata.table.name.toLowerCase() === "placement"
          ? (sortPlacementRowsByPublisherName(
              tsrows,
              publisherNameSorting,
              (publisherId) => this.getPublisherName(publisherId)
            ) as WithDbId<TSRow>[])
          : tsrows;
      this.loadedRows = {
        items: sortedRows,
        current_offset: dbrows.current_offset,
        total_size: dbrows.total_size,
      };
      this.rowsLoading = false;
    }
  };

  getPublisherName = (publisherId: string): string => {
    return this.publisherNameById[publisherId] ?? "";
  };

  private resolveTableQueryFilter = async (
    tableName: string,
    filter: FieldPredicate
  ): Promise<FieldPredicate> => {
    if (tableName.toLowerCase() !== "placement") {
      return filter;
    }
    return resolvePlacementTableFilter(this.service, filter);
  };

  private cachePublisherNamesForPlacements = async (
    tsrows: WithDbId<TSRow>[]
  ): Promise<void> => {
    const publisherIds = new Set<string>();
    for (const row of tsrows) {
      const publisherId = getPlacementPublisherId(row.value);
      if (publisherId && !this.publisherNameById[publisherId]) {
        publisherIds.add(publisherId);
      }
    }
    if (publisherIds.size === 0) {
      return;
    }

    const dbrows = await this.service.adminQuery({
      table: "publisher",
      columns: [],
      query: makeTableQuery({
        filter: {
          kind: "in",
          value: makeFieldIn({
            field: "id",
            values: [...publisherIds],
          }),
        },
        count: publisherIds.size,
      }),
    });

    const next = { ...this.publisherNameById };
    for (const row of dbrows.items) {
      const name = row.value?.name;
      if (typeof name === "string") {
        next[row.id] = name;
      }
    }
    this.publisherNameById = next;
  };

  /** Walk all placements matching the current filter in stable batches. */
  private forEachMatchingPlacementBatch = async (
    onBatch: (items: WithDbId<TSRow>[]) => void | Promise<void>
  ): Promise<number> => {
    if (this.lastQuery === null) {
      return 0;
    }

    const { tmetadata, view } = this.lastQuery;
    const resolvedFilter = await this.resolveTableQueryFilter(
      tmetadata.table.name,
      view.filter
    );
    const { apiSorting } = resolvePlacementTableSorting(view.sorting);
    let offset = 0;
    let totalSize = 0;

    do {
      const query = makeTableQuery({
        offset,
        count: PLACEMENT_QUERY_BATCH_SIZE,
      });
      query.filter = resolvedFilter;
      query.sorting = withStableIdSort(apiSorting);

      const dbrows = await this.service.adminQuery({
        table: tmetadata.table.name,
        columns: [],
        query,
      });

      await onBatch(dbrows.items);
      totalSize = dbrows.total_size;
      offset += dbrows.items.length;
      if (dbrows.items.length === 0) {
        break;
      }
    } while (offset < totalSize);

    return totalSize;
  };

  private fetchPlacementDbRowsByIds = async (
    ids: string[]
  ): Promise<Map<string, DbRow>> => {
    if (this.lastQuery === null || ids.length === 0) {
      return new Map();
    }

    const { tmetadata } = this.lastQuery;
    const byId = new Map<string, DbRow>();

    for (
      let start = 0;
      start < ids.length;
      start += PLACEMENT_QUERY_BATCH_SIZE
    ) {
      const batch = ids.slice(start, start + PLACEMENT_QUERY_BATCH_SIZE);
      const dbrows = await this.service.adminQuery({
        table: tmetadata.table.name,
        columns: [],
        query: makeTableQuery({
          filter: {
            kind: "in",
            value: makeFieldIn({
              field: "id",
              values: batch,
            }),
          },
          count: batch.length,
        }),
      });

      for (const row of dbrows.items) {
        byId.set(row.id, row.value);
      }
    }

    return byId;
  };

  /** Count placements matching current filter: how many will be updated (non-deprecated) vs deprecated. */
  queryPlacementEditAllCounts = async (): Promise<{
    willBeUpdated: number;
    deprecated: number;
  }> => {
    let deprecated = 0;
    const totalSize = await this.forEachMatchingPlacementBatch((items) => {
      for (const row of items) {
        const v = row.value as Record<string, unknown>;
        if (v.is_deprecated === true || v.isDeprecated === true) {
          deprecated += 1;
        }
      }
    });

    return {
      willBeUpdated: totalSize - deprecated,
      deprecated,
    };
  };

  queryAllMatchingIdsForPlacementEditAll = async (): Promise<{
    idsToUpdate: string[];
  }> => {
    const idsToUpdate: string[] = [];
    await this.forEachMatchingPlacementBatch((items) => {
      for (const row of items) {
        idsToUpdate.push(row.id);
      }
    });
    return { idsToUpdate };
  };

  /** Placement rows for JSON export by explicit id list (preserves selection order). */
  exportPlacementRowsByIds = async (
    ids: string[]
  ): Promise<Record<string, unknown>[]> => {
    if (this.lastQuery === null || ids.length === 0) {
      return [];
    }

    const { jsonBinding } = this.lastQuery.tmetadata;
    const fetched = await this.fetchPlacementDbRowsByIds(ids);

    return ids
      .map((id) => {
        const dbRow = fetched.get(id);
        return dbRow === undefined
          ? undefined
          : toPlacementExportJson(id, jsonBinding, dbRow);
      })
      .filter((row): row is Record<string, unknown> => row !== undefined);
  };

  clearPlacementExportSelection = (): void => {
    this.selectedPlacementIds.clear();
  };

  setPlacementSelection = (id: string, selected: boolean): void => {
    if (selected) {
      this.selectedPlacementIds.add(id);
    } else {
      this.selectedPlacementIds.delete(id);
    }
  };

  toggleSelectAllPlacementsMatchingFilter = async (): Promise<void> => {
    const hasSelection = this.selectedPlacementIds.size > 0;
    runInAction(() => {
      this.placementSelectAllLoading = true;
    });
    try {
      if (hasSelection) {
        runInAction(() => {
          this.clearPlacementExportSelection();
        });
        return;
      }

      const { idsToUpdate } =
        await this.queryAllMatchingIdsForPlacementEditAll();
      runInAction(() => {
        this.selectedPlacementIds.clear();
        for (const id of idsToUpdate) {
          this.selectedPlacementIds.add(id);
        }
      });
    } finally {
      runInAction(() => {
        this.placementSelectAllLoading = false;
      });
    }
  };

  bulkUpdate = async (
    tableName: string,
    ids: string[],
    values: DbRow
  ): Promise<DbResult<Unit>> => {
    if (ids.length === 0) {
      return makeDbResult<Unit, "ok">("ok", makeUnit({}));
    }

    for (const id of ids) {
      const result = await this.service.adminUpdate({
        table: tableName,
        values: {
          id,
          value: values,
        },
      });
      if (result.kind !== "ok") {
        return result;
      }
    }

    return makeDbResult<Unit, "ok">("ok", makeUnit({}));
  };

  getOrganisationRawConfig(id: string): unknown {
    return this.organisationRawConfigByKey[id];
  }

  setView = async (view: TableView): Promise<void> => {
    if (this.lastQuery !== null) {
      this.lastQuery.view = view;
      await this.loadRows();
    }
    return;
  };

  // @computed
  getTableView = (): TableView | null => {
    return this.lastQuery && this.lastQuery.view;
  };

  // @computed
  areColumnPropsShown = (columnName: string): boolean => {
    return this.columnPropsShown === columnName;
  };

  setColumnPropsShown = (columnName: string | null): void => {
    this.columnPropsShown = columnName;
  };

  create = async (
    // eslint-disable-next-line no-use-before-define
    tmetadata: TableMetadata,
    tsrow: TSRow
  ): Promise<DbResult<string>> => {
    const dbresult = await this.service.adminCreate({
      table: tmetadata.table.name,
      values: tmetadata.jsonBinding.toJson(tsrow) as DbRow,
    });
    if (dbresult.kind === "ok") {
      await this.loadRows();
    }
    return dbresult;
  };

  update = async (
    // eslint-disable-next-line no-use-before-define
    tmetadata: TableMetadata,
    tsrow: WithDbId<TSRow>
  ): Promise<DbResult<Unit>> => {
    let valueToSerialize = tsrow.value;

    // Capture allowedPublisherIdsForSearch early, before any transformations that might drop it.
    // This is critical for Edit Raw mode where jsonBinding.fromJson might not preserve it correctly.
    let capturedAllowedPublisherIds: string[] | undefined = undefined;
    if (
      tmetadata.table.name === "organisation" &&
      valueToSerialize &&
      typeof valueToSerialize === "object" &&
      valueToSerialize.config != null &&
      typeof valueToSerialize.config === "object"
    ) {
      const config = valueToSerialize.config as {
        hideJobId?: boolean;
        allowedPublisherIdsForSearch?: string[];
      };
      if (Array.isArray(config.allowedPublisherIdsForSearch)) {
        capturedAllowedPublisherIds = config.allowedPublisherIdsForSearch;
      }
    }

    // Ensure organisation config always includes allowedPublisherIdsForSearch when updating.
    // Edit Raw can add it, but it was sometimes dropped before reaching the API (e.g. form
    // round-trip or binding only outputting known-by-form fields). Normalise before serialise.
    if (
      tmetadata.table.name === "organisation" &&
      valueToSerialize &&
      typeof valueToSerialize === "object" &&
      valueToSerialize.config != null &&
      typeof valueToSerialize.config === "object"
    ) {
      const config = valueToSerialize.config as {
        hideJobId?: boolean;
        allowedPublisherIdsForSearch?: string[];
      };
      valueToSerialize = {
        ...valueToSerialize,
        config: {
          ...config,
          hideJobId: config.hideJobId ?? false,
          allowedPublisherIdsForSearch:
            capturedAllowedPublisherIds ??
            (Array.isArray(config.allowedPublisherIdsForSearch)
              ? config.allowedPublisherIdsForSearch
              : []),
        },
      };
    }

    let rowJson = tmetadata.jsonBinding.toJson(valueToSerialize) as DbRow & {
      config?: { allowedPublisherIdsForSearch?: string[] };
    };

    // Force config.allowedPublisherIdsForSearch into the payload for organisation.
    // The table jsonBinding can omit it if the form/veditor never touched that field.
    // Use the captured value to ensure we don't lose data from Edit Raw mode.
    if (
      tmetadata.table.name === "organisation" &&
      rowJson &&
      typeof rowJson === "object"
    ) {
      const fromConfig =
        valueToSerialize?.config != null &&
        typeof valueToSerialize.config === "object"
          ? (
              valueToSerialize.config as {
                allowedPublisherIdsForSearch?: string[];
              }
            ).allowedPublisherIdsForSearch
          : undefined;
      const idsToUse = capturedAllowedPublisherIds ?? fromConfig;
      const nextConfig =
        rowJson.config != null && typeof rowJson.config === "object"
          ? { ...rowJson.config }
          : {};
      nextConfig.allowedPublisherIdsForSearch = Array.isArray(idsToUse)
        ? idsToUse
        : [];
      rowJson = { ...rowJson, config: nextConfig };
    }

    const dbresult = await this.service.adminUpdate({
      table: tmetadata.table.name,
      values: {
        id: tsrow.id,
        value: tmetadata.jsonBinding.toJson(tsrow.value) as DbRow,
      },
    });
    if (dbresult.kind === "ok") {
      await this.loadRows();
    }
    return dbresult;
  };

  delete = async (
    // eslint-disable-next-line no-use-before-define
    tmetadata: TableMetadata,
    id: DbKey<TSRow>
  ): Promise<DbResult<Unit>> => {
    const dbresult = await this.service.adminDelete({
      table: tmetadata.table.name,
      id,
    });
    if (dbresult.kind === "ok") {
      await this.loadRows();
    }
    return dbresult;
  };

  //  @computed
  getLoadedRows = (): WithDbId<TSRow>[] => {
    if (this.loadedRows) {
      return this.loadedRows.items;
    }
    return [];
  };

  //  @computed
  // eslint-disable-next-line no-use-before-define
  getTables = (): Loading<Table[]> => {
    return mapLoading((m) => {
      const tables: Table[] = [];
      m.tableNames.forEach((tn) => {
        const table = m.tableMap[tn];
        if (table !== undefined) {
          tables.push(table);
        }
      });
      return tables;
    }, this.metadata);
  };

  //  @computed
  canPageBack = (): boolean => {
    return (
      !this.rowsLoading &&
      this.loadedRows !== null &&
      this.loadedRows.current_offset > 0
    );
  };

  pageBack = async (): Promise<void> => {
    if (this.canPageBack() && this.lastQuery !== null) {
      this.lastQuery.query.query.offset -= this.pageSize;
      if (this.lastQuery.query.query.offset < 0) {
        this.lastQuery.query.query.offset = 0;
      }
    }
    await this.loadRows();
  };

  //  @computed
  canPageForward = (): boolean => {
    return (
      !this.rowsLoading &&
      this.loadedRows !== null &&
      this.loadedRows.current_offset + this.loadedRows.items.length <
        this.loadedRows.total_size
    );
  };

  pageForward = async (): Promise<void> => {
    if (this.canPageForward() && this.lastQuery !== null) {
      this.lastQuery.query.query.offset += this.pageSize;
    }
    await this.loadRows();
  };

  getActions() {
    if (this.actions.length === 0 && this.metadata.kind === "ready") {
      // eslint-disable-next-line no-use-before-define
      const ctx: ActionContext = {
        resolver: this.appDeclResolver,
        customize: appCustomize(this.metadata.value, this.tableHrefFn),
      };
      this.actions = this.actionFactories.map((af) => {
        return af(ctx);
      });
    }
    return this.actions;
  }
}

export function mapLoading<A, B>(fn: (a: A) => B, la: Loading<A>): Loading<B> {
  if (la.kind === "loading") {
    return la;
  }
  return { kind: "ready", value: fn(la.value) };
}

export function createTableMetadata(
  metadata: Metadata,
  tableName: string,
  tableHrefFn: TableHrefFn
): TableMetadata {
  const table = metadata.tableMap[tableName];
  if (table === undefined) {
    throw new Error("Unknown table " + tableName);
  }
  const tableScopedDecl = scopedDeclFromTable(table);
  const tableScopedName = {
    moduleName: tableScopedDecl.moduleName,
    name: tableScopedDecl.decl.name,
  };
  const tableTypeExpr: ATypeExpr<TSRow> = {
    value: {
      typeRef: { kind: "reference", value: tableScopedName },
      parameters: [],
    },
  };
  function resolver(scopedName: adlast.ScopedName): adlast.ScopedDecl {
    if (scopedNamesEqual(scopedName, tableScopedName)) {
      return tableScopedDecl;
    } else {
      return metadata.resolver(scopedName);
    }
  }
  const customize = appCustomize(metadata, tableHrefFn);
  const veditor: VEditor<TSRow, unknown, unknown> = createVEditor(
    tableTypeExpr,
    resolver,
    customize
  );
  const jsonBinding = createJsonBinding(resolver, tableTypeExpr);
  return {
    table,
    resolver: metadata.resolver,
    veditor,
    jsonBinding,
    customize,
  };
}

export const SYS_CUSTOMIZE = sysCustomize({
  dateFormat: DDSMMSYYYY_FORMAT,
});

function appCustomize(metadata: Metadata, tableHrefFn: TableHrefFn): Customize {
  return {
    getCustomVEditor: (ctx: CustomContext) => {
      const dbKeyTableName = getDbKeyTableName(ctx.typeExpr, metadata);
      if (dbKeyTableName !== null) {
        return dbKeyVEditor({
          tableName: dbKeyTableName,
          tableHref: tableHrefFn(dbKeyTableName),
        });
      }
      return SYS_CUSTOMIZE.getCustomVEditor(ctx);
    },
    getCustomField: (ctx: CustomContext) => {
      const dbKeyTableName = getDbKeyTableName(ctx.typeExpr, metadata);
      if (dbKeyTableName !== null) {
        return dbKeyFieldFns(dbKeyTableName);
      }
      return SYS_CUSTOMIZE.getCustomField(ctx);
    },
    getHideVectorReorder: (ctx: CustomContext) =>
      ctx.field?.name === "allowedPublisherIdsForSearch",
    getUseGrabCursor: (ctx: CustomContext) =>
      ctx.field?.name === "allowedPublisherIdsForSearch",
  };
}

// Return the associated table name if the typeExpression is DbKey<T>, otherwise
// return null
function getDbKeyTableName(
  typeExpr: adlast.TypeExpr,
  metadata: Metadata
): string | null {
  let tableName: string | null = null;
  if (
    typeExpr.typeRef.kind === "reference" &&
    scopedNamesEqual(typeExpr.typeRef.value, snDbKey) && // Custom veditor for a db key
    typeExpr.parameters[0].typeRef.kind === "reference"
  ) {
    const sn = typeExpr.parameters[0].typeRef.value;
    for (const tn of metadata.tableNames) {
      const table = metadata.tableMap[tn];
      if (
        table &&
        table.declModuleName === sn.moduleName &&
        table.declName === sn.name
      ) {
        tableName = table.name;
        break;
      }
    }
  }
  return tableName;
}

function scopedDeclFromTable(table: Table): adlast.ScopedDecl {
  const struct = adlast.makeStruct({
    typeParams: [],
    fields: table.columns.map((tcol) => {
      return adlast.makeField({
        name: tcol.name,
        serializedName: tcol.name,
        typeExpr: tcol.typeExpr,
        default: tcol.defaultValue,
        annotations: [],
      });
    }),
  });
  const decl = adlast.makeDecl({
    name: table.declName,
    version: { kind: "nothing" },
    type_: { kind: "struct_", value: struct },
    annotations: [],
  });
  return {
    moduleName: "__LOCAL__",
    decl,
  };
}

export interface TActionData<I, O> {
  method: string;
  path: string;
  description: string;
  veditorReq: VEditor<I, unknown, unknown>;
  jsonBindingReq: JsonBinding<I>;
  call(req: I): Promise<O>;
  veditorResp: VEditor<O, unknown, unknown>;
  jsonBindingResp: JsonBinding<O>;
}

export type ActionData = TActionData<unknown, unknown>;

export interface ActionContext {
  resolver: DeclResolver;
  customize: Customize;
}

export type ActionFactory = (ctx: ActionContext) => ActionData;

interface PostFn<I, O> {
  description(): string;
  rtype: common_http.HttpPost<I, O>;
  call(req: I): Promise<O>;
}

export function makePostAction<I, O>(details: PostFn<I, O>): ActionFactory {
  return (ctx: ActionContext) => {
    const veditorReq: VEditor<I, unknown, unknown> = createVEditor(
      details.rtype.reqType,
      ctx.resolver,
      ctx.customize
    );
    const jsonBindingReq = createJsonBinding(
      ctx.resolver,
      details.rtype.reqType
    );
    const veditorResp: VEditor<O, unknown, unknown> = createVEditor(
      details.rtype.respType,
      ctx.resolver,
      ctx.customize
    );
    const jsonBindingResp = createJsonBinding(
      ctx.resolver,
      details.rtype.respType
    );

    return {
      method: "post",
      path: details.rtype.path,
      description: details.description(),
      veditorReq,
      jsonBindingReq,
      veditorResp,
      jsonBindingResp,
      call: details.call,
    };
  };
}
